// ** React
import { useState, type SyntheticEvent, Fragment } from 'react';

// ** MUI Components
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Box from '@mui/material/Box';

// ** Components
import FaIconUvilo from '@erikdakoda/ui/components/FaIconUvilo';
import TextSizeToggler from '@erikdakoda/site-template-ui/components/TextSizeToggler';
import UserAvatar, { type UserForAvatar } from '@erikdakoda/ui/components/UserAvatar';
import ClipboardButton from '@erikdakoda/ui/components/ClipboardButton';
import PrivacyToggler from '@erikdakoda/site-template-ui/components/PrivacyToggler';
import AdminOnly from '@erikdakoda/ui/components/AdminOnly';
import HoverListItem from '@erikdakoda/ui/components/HoverListItem';
import DisableAdminToggler from '@erikdakoda/site-template-ui/components/DisableAdminToggler';

// ** Hooks
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';

// ** Context
import authConfig from '@erikdakoda/auth/shared/config';
import { type Settings } from '@erikdakoda/site-template-ui/context/settingsContext';
import { signOut } from '@erikdakoda/auth/client/betterAuthClient';

// ** Utils
import clsx from 'clsx';
import { getUserName } from '@erikdakoda/auth/shared/utilities';

interface UserDropdownProps {
  settings: Settings;
  saveSettings: (values: Settings) => void;
}

const UserDropdown = ({ settings }: UserDropdownProps) => {
  'use memo';

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // ** Hooks
  const router = useRouter();
  const { user, isUserLoading } = useCurrentUser();
  const { t } = useTranslation();

  // ** Vars
  const { direction } = settings;

  const handleDropdownOpen = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = (url?: string) => {
    if (url) {
      router.push(url);
    }
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    setIsLoading(true);
    setAnchorEl(null);

    await signOut({
      fetchOptions: {
        onSuccess: () => {
          router.push(authConfig.config.pages.login);
        },
      },
    });

    setIsLoading(false);
  };

  return (
    <Fragment>
      <List sx={{ py: 0 }}>
        <ListItemButton
          aria-haspopup="true"
          className={clsx('user-menu-title', {
            'Mui-selected': Boolean(anchorEl),
          })}
          tabIndex={0}
          selected={Boolean(anchorEl)}
          onClick={handleDropdownOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            px: 1,
            py: 1,
            borderRadius: 24,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&:focus-visible': {
              outline: 0,
              backgroundColor: 'action.focus',
            },
            '&.Mui-selected': {
              backgroundColor: 'action.selected',
            },
          }}
        >
          <UserAvatar
            user={user as unknown as UserForAvatar}
            isLoading={isUserLoading || isLoading}
            sx={{ padding: 1 }}
          />
        </ListItemButton>
      </List>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleDropdownClose()}
        sx={{
          '& .MuiMenu-paper': {
            width: 230,
            mt: '4px',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderTop: 'none',
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
      >
        {!user && [
          <MenuItem key="login" onClick={() => handleDropdownClose('login')}>
            <ListItemIcon>
              <FaIconUvilo icon="right-from-bracket" />
            </ListItemIcon>
            <ListItemText>{t('log-in')}</ListItemText>
          </MenuItem>,
          <MenuItem key="signup" onClick={() => handleDropdownClose('signup')}>
            <ListItemIcon>
              <FaIconUvilo icon="user-plus" />
            </ListItemIcon>
            <ListItemText>{t('sign-up')}</ListItemText>
          </MenuItem>,
          <Divider key="guest-divider" sx={{ mb: '0 !important' }} />,
        ]}
        {user && [
          <HoverListItem
            key="profile-header"
            padded
            secondaryAction={
              <AdminOnly>
                <ClipboardButton text={user?.id || ''} title={`Copy userId '${user?.id}'`} />
              </AdminOnly>
            }
          >
            <ListItemAvatar>
              <UserAvatar user={user as unknown as UserForAvatar} />
            </ListItemAvatar>
            <ListItemText
              disableTypography
              secondary={<DisableAdminToggler sx={{ marginTop: '6px' }} />}
              slotProps={{
                root: { sx: { lineHeight: 1 } },
              }}
            >
              {getUserName(user)}
            </ListItemText>
          </HoverListItem>,
          <Divider key="profile-divider" sx={{ mb: '0 !important' }} />,
        ]}
        {/* <Box p="12px 16px 6px">
          <ModeToggler settings={settings} saveSettings={saveSettings} fullWidth />
        </Box> */}
        {user && [
          <Box
            key="togglers"
            sx={{
              px: 2,
              py: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <TextSizeToggler fullWidth />
            <PrivacyToggler />
          </Box>,
          <Divider key="togglers-divider" sx={{ mt: '0 !important' }} />,
          <MenuItem key="settings" onClick={() => handleDropdownClose('/user/settings/security')}>
            <ListItemIcon>
              <FaIconUvilo icon="gear-complex" />
            </ListItemIcon>
            <ListItemText>{t('user-settings')}</ListItemText>
          </MenuItem>,
          <MenuItem key="convos" onClick={() => handleDropdownClose('/user/convos')}>
            <ListItemIcon>
              <FaIconUvilo icon="message-lines" />
            </ListItemIcon>
            <ListItemText>{t('convos')}</ListItemText>
          </MenuItem>,
          <MenuItem key="todos" onClick={() => handleDropdownClose('/user/todo')}>
            <ListItemIcon>
              <FaIconUvilo icon="clipboard-check" />
            </ListItemIcon>
            <ListItemText>Todos</ListItemText>
          </MenuItem>,
        ]}
        <MenuItem onClick={() => handleDropdownClose('/user/pricing')}>
          <ListItemIcon>
            <FaIconUvilo icon="money-bill-wave" />
          </ListItemIcon>
          <ListItemText>{t('pricing')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDropdownClose('/user/faq')}>
          <ListItemIcon>
            <FaIconUvilo icon="life-ring" />
          </ListItemIcon>
          <ListItemText>{t('faq')}</ListItemText>
        </MenuItem>
        {user && [
          <Divider key="logout-divider" />,
          <MenuItem key="logout" onClick={handleLogout}>
            <ListItemIcon>
              <FaIconUvilo icon="right-from-bracket" />
            </ListItemIcon>
            <ListItemText>{t('log-out')}</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </Fragment>
  );
};

export default UserDropdown;
